import { useUpdateJobItemStatus } from '@/api/Jobs';
import { useAppCustomers } from '@/hooks';
import { JobItem, JobStatus } from '@/types';
import { CustomColumns, CustomTable } from '@/ui/components/CustomTable';
import { timeConverter } from '@/utils/helpers';
import { App } from 'antd';
import React, { FC, useMemo } from 'react';
import {
  canTransitionJobStatus,
  getJobStatusDisplayLabel,
  getJobStatusSelectOptions,
  isTerminalJobStatus,
} from '../../utils/jobStatusWorkflow';
import { LogsTimeline } from './LogsTimeline';

interface JobItemsDataGridProps {
  jobId: number;
  items: JobItem[];
  jobStatus?: JobStatus[];
}

export const JobItemsDataGrid: FC<JobItemsDataGridProps> = ({
  jobId,
  items,
  jobStatus,
}) => {
  const { hasAdminAccess } = useAppCustomers();
  const { message } = App.useApp();
  const { mutateAsync: updateJobItemStatusAsync, isPending: isLoading } =
    useUpdateJobItemStatus();

  const columns: CustomColumns<JobItem> = useMemo(
    () => [
      {
        dataIndex: 'templateName',
        title: 'Name',
        sortable: true,
        filter: true,
        width: 150,
        fixed: 'left',
      },
      {
        dataIndex: 'templateDescription',
        title: 'Description',
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        dataIndex: 'jobStatusId',
        title: 'Status',
        editable: (record) =>
          !!hasAdminAccess && !isTerminalJobStatus(record.jobStatusId),
        renderType: 'select',
        renderOptions: (record) =>
          getJobStatusSelectOptions(record.jobStatusId, jobStatus),
        render: (_, record) =>
          getJobStatusDisplayLabel(
            record.jobStatusId,
            record.jobStatus,
            jobStatus,
          ),
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        dataIndex: 'createdAt',
        title: 'Create Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
      },
      {
        dataIndex: 'createdBy',
        title: 'Created By',
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        dataIndex: 'updatedAt',
        title: 'Update Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
      },
      {
        dataIndex: 'updatedBy',
        title: 'Updated By',
        sortable: true,
        filter: true,
        width: 200,
      },
      {
        dataIndex: 'completedAt',
        title: 'Complete Date',
        renderType: 'date',
        width: 180,
        sortable: true,
        filter: true,
        render: (value) => timeConverter(value),
      },
      {
        dataIndex: 'completedBy',
        title: 'Completed By',
        sortable: true,
        filter: true,
        width: 200,
      },
    ],
    [hasAdminAccess, jobStatus],
  );

  const onRowUpdating = async (oldData: JobItem, newData: Partial<JobItem>) => {
    const nextId = newData.jobStatusId!;
    if (!canTransitionJobStatus(oldData.jobStatusId, nextId)) {
      message.open({
        type: 'warning',
        content: 'This status change is not allowed.',
      });
      return;
    }
    const res = await updateJobItemStatusAsync({
      jobId: jobId,
      jobItemId: oldData.jobItemId,
      jobStatusId: nextId,
    });
    const success = res.data;
    message.open({
      type: success ? 'success' : 'error',
      content: success
        ? 'Status updated successfully!'
        : 'Failed to update status:' + res.message?.Message,
    });
  };

  return (
    <CustomTable
      dataSource={items}
      columns={columns}
      rowKey="jobItemId"
      loading={isLoading}
      onUpdate={(oldData, newData) => onRowUpdating(oldData, newData)}
      expandable={{
        expandedRowRender: (data) => <LogsTimeline logs={data.logs} />,
      }}
      scroll={{ x: '100%' }}
      pagination={false}
    />
  );
};
